import { useState, useEffect } from "react";
import { PaymentDetail, PaymentMethod } from "../../ws/types";
import { rpcCall } from "../../ws/client";
import { toast } from "../ui/Toast";
import "../../styles/modals/PaymentDetailsModal.scss";

interface Props {
    orderId: string;
    message?: string;
    amount?: number;
    wallet: string;
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (paymentDetailId?: string) => Promise<void>;
}

const paymentMethodLabels: Record<PaymentMethod, string> = {
    Sber: "Сбербанк",
    Tinkoff: "Тинькофф",
    Raiffeisen: "Райффайзен",
    Alpha: "Альфа-Банк",
    Ozon: "Озон Банк",
    SBP: "СБП"
};

export default function PaymentDetailsModal({
    orderId,
    message,
    amount,
    wallet,
    isOpen,
    onClose,
    onSubmit
}: Props) {
    const [paymentDetails, setPaymentDetails] = useState<PaymentDetail[]>([]);
    const [selectedPaymentDetailId, setSelectedPaymentDetailId] = useState<string>("");
    const [showNewPaymentForm, setShowNewPaymentForm] = useState(false);
    const [newPaymentDetail, setNewPaymentDetail] = useState({
        payment_method: "Sber" as PaymentMethod,
        account_number: "",
        account_name: "",
        bank_name: "",
        additional_info: ""
    });

    useEffect(() => {
        if (isOpen && wallet) {
            loadPaymentDetails();
        }
    }, [isOpen, wallet]);

    const loadPaymentDetails = async () => {
        if (!wallet) return;
        
        try {
            const response = await rpcCall<
                { wallet: string; active_only?: boolean },
                { payment_details: PaymentDetail[] }
            >("payment_details.list", {
                wallet: wallet,
                active_only: true
            });

            if (response.result?.payment_details) {
                setPaymentDetails(response.result.payment_details);
            }
        } catch (error) {
            console.error("Failed to load payment details:", error);
        }
    };

    const handleSubmit = async () => {
        // Если выбран существующий реквизит
        if (selectedPaymentDetailId) {
            await onSubmit(selectedPaymentDetailId);
            resetForm();
            return;
        }

        // Если создается новый реквизит
        if (showNewPaymentForm) {
            if (!newPaymentDetail.account_number || !newPaymentDetail.account_name) {
                toast.error("Заполните обязательные поля");
                return;
            }

            try {
                // Создаем новый реквизит
                const createResponse = await rpcCall("payment_details.create", {
                    wallet: wallet,
                    ...newPaymentDetail
                });

                if (createResponse.error) {
                    toast.error(createResponse.error.message);
                    return;
                }

                // Получаем ID созданного реквизита
                const createdDetail = (createResponse.result as any)?.payment_detail;
                if (!createdDetail?.id) {
                    toast.error("Не удалось создать реквизиты");
                    return;
                }

                // Отправляем deal request с новым реквизитом
                await onSubmit(createdDetail.id);
                resetForm();
            } catch (err) {
                console.error(err);
                toast.error("Ошибка при создании реквизитов");
            }
            return;
        }

        toast.error("Выберите существующий реквизит или создайте новый");
    };

    const resetForm = () => {
        setSelectedPaymentDetailId("");
        setShowNewPaymentForm(false);
        setNewPaymentDetail({
            payment_method: "Sber",
            account_number: "",
            account_name: "",
            bank_name: "",
            additional_info: ""
        });
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={handleClose}>
            <div className="modal-content payment-details-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Укажите платежные реквизиты</h3>
                    <button className="close-btn" onClick={handleClose}>
                        ✕
                    </button>
                </div>
                <div className="modal-body">
                    <p className="modal-description">
                        Как продавец, вы должны указать реквизиты для получения оплаты от покупателя
                    </p>

                    {!showNewPaymentForm ? (
                        <div className="existing-details">
                            <h4>Выберите сохраненные реквизиты:</h4>
                            {paymentDetails.length > 0 ? (
                                <>
                                    {paymentDetails.map((detail) => (
                                        <label key={detail.id} className="payment-detail-option">
                                            <input
                                                type="radio"
                                                name="payment_detail"
                                                value={detail.id}
                                                checked={selectedPaymentDetailId === detail.id}
                                                onChange={(e) => setSelectedPaymentDetailId(e.target.value)}
                                                aria-label={`Выбрать реквизиты ${detail.payment_method}`}
                                            />
                                            <div className="detail-info">
                                                <div className="detail-method">{paymentMethodLabels[detail.payment_method]}</div>
                                                <div className="detail-account">{detail.account_number}</div>
                                                <div className="detail-name">{detail.account_name}</div>
                                            </div>
                                        </label>
                                    ))}
                                </>
                            ) : (
                                <p className="no-payment-details">У вас пока нет сохраненных реквизитов</p>
                            )}
                            <button
                                className="btn-link"
                                onClick={() => {
                                    setShowNewPaymentForm(true);
                                    setSelectedPaymentDetailId("");
                                }}
                            >
                                + Добавить новые реквизиты
                            </button>
                        </div>
                    ) : (
                        <div className="new-payment-form">
                            <button
                                className="btn-link"
                                onClick={() => setShowNewPaymentForm(false)}
                            >
                                ← Назад к списку
                            </button>
                            <h4>Новые реквизиты:</h4>
                            <div className="form-group">
                                <label htmlFor="payment-method-select">Платежная система *</label>
                                <select
                                    id="payment-method-select"
                                    value={newPaymentDetail.payment_method}
                                    onChange={(e) =>
                                        setNewPaymentDetail({
                                            ...newPaymentDetail,
                                            payment_method: e.target.value as PaymentMethod
                                        })
                                    }
                                >
                                    {Object.entries(paymentMethodLabels).map(([value, label]) => (
                                        <option key={value} value={value}>
                                            {label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Номер счета/карты *</label>
                                <input
                                    type="text"
                                    value={newPaymentDetail.account_number}
                                    onChange={(e) =>
                                        setNewPaymentDetail({
                                            ...newPaymentDetail,
                                            account_number: e.target.value
                                        })
                                    }
                                    placeholder="1234 5678 9012 3456"
                                />
                            </div>
                            <div className="form-group">
                                <label>ФИО владельца *</label>
                                <input
                                    type="text"
                                    value={newPaymentDetail.account_name}
                                    onChange={(e) =>
                                        setNewPaymentDetail({
                                            ...newPaymentDetail,
                                            account_name: e.target.value
                                        })
                                    }
                                    placeholder="Иванов Иван Иванович"
                                />
                            </div>
                            <div className="form-group">
                                <label>Название банка</label>
                                <input
                                    type="text"
                                    value={newPaymentDetail.bank_name}
                                    onChange={(e) =>
                                        setNewPaymentDetail({
                                            ...newPaymentDetail,
                                            bank_name: e.target.value
                                        })
                                    }
                                    placeholder="ПАО Сбербанк"
                                />
                            </div>
                        </div>
                    )}
                </div>
                <div className="modal-footer">
                    <button
                        className="btn btn-secondary"
                        onClick={handleClose}
                    >
                        Отмена
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={handleSubmit}
                        disabled={!selectedPaymentDetailId && (!showNewPaymentForm || !newPaymentDetail.account_number || !newPaymentDetail.account_name)}
                    >
                        Отправить заявку
                    </button>
                </div>
            </div>
        </div>
    );
}
